import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Navigate, useNavigate } from "react-router";
import { releaseAccessBrandUrlSchema } from "@runcheetah/shared";
import { Button, Input } from "@runcheetah/ui";
import { Loader2, LogOut } from "lucide-react";

import { signOut } from "@/lib/auth-client";
import { trpc } from "@/lib/trpc";

export default function PrivateBetaPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const statusQuery = trpc.releaseAccess.status.useQuery(undefined, { staleTime: 30_000 });
  const [brandUrl, setBrandUrl] = useState("");
  const [error, setError] = useState("");

  const joinWaitlist = trpc.releaseAccess.joinWaitlist.useMutation({
    onSuccess: async () => {
      setError("");
      await utils.releaseAccess.status.invalidate();
    },
    onError: (mutationError) => setError(mutationError.message),
  });

  const waitlistEntry = statusQuery.data?.waitlistEntry;

  useEffect(() => {
    if (waitlistEntry?.brandUrl) {
      setBrandUrl(waitlistEntry.brandUrl);
    }
  }, [waitlistEntry?.brandUrl]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");

    const result = releaseAccessBrandUrlSchema.safeParse(brandUrl);
    if (!result.success) {
      setError(
        intl.formatMessage({
          id: "privateBeta.invalidBrandUrl",
          defaultMessage: "Please enter a valid website URL.",
        }),
      );
      return;
    }

    joinWaitlist.mutate({ brandUrl: result.data });
  };

  const handleSignOut = async () => {
    await signOut({
      fetchOptions: {
        onSuccess: () => {
          void utils.invalidate();
          window.location.assign("/");
        },
        onError: () => {
          navigate("/", { replace: true });
        },
      },
    });
  };

  const signOutControl = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="rounded-full border border-[rgba(13,31,45,0.1)] bg-white/70 px-4 font-semibold text-[rgba(13,31,45,0.7)] shadow-sm backdrop-blur transition-all hover:-translate-y-0.5 hover:bg-white hover:text-[#0D1F2D]"
      onClick={() => void handleSignOut()}
    >
      <LogOut className="h-4 w-4" />
      <FormattedMessage id="sidebar.signOut" defaultMessage="Sign out" />
    </Button>
  );
  const isSubmitting = joinWaitlist.isPending;

  if (statusQuery.isPending) {
    return (
      <div className="relative flex flex-1 overflow-hidden bg-[#F6F1EA] px-4 py-6 sm:px-6 sm:py-8">
        <div className="pointer-events-none absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full bg-brand/30 blur-3xl" />
        <div className="pointer-events-none absolute bottom-[-12rem] right-[-8rem] h-96 w-96 rounded-full bg-[#0D1F2D]/10 blur-3xl" />
        <div className="relative z-10 mx-auto flex w-full max-w-6xl flex-1 flex-col">
          <div className="flex justify-end">{signOutControl}</div>
          <div className="flex min-h-[60vh] flex-1 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-[#0D1F2D]/60" />
          </div>
        </div>
      </div>
    );
  }

  if (statusQuery.data?.allowed) {
    return <Navigate to="/app" replace />;
  }

  return (
    <div className="relative flex flex-1 overflow-hidden bg-[#F6F1EA] px-4 py-6 text-[#0D1F2D] sm:px-6 sm:py-8">
      <div className="pointer-events-none absolute left-1/2 top-0 h-80 w-80 -translate-x-1/2 rounded-full bg-brand/35 blur-3xl" />
      <div className="pointer-events-none absolute bottom-[-12rem] right-[-8rem] h-[28rem] w-[28rem] rounded-full bg-[#0D1F2D]/10 blur-3xl" />
      <div className="pointer-events-none absolute left-6 top-24 hidden h-24 w-24 rounded-full border border-[#0D1F2D]/10 lg:block" />

      <div className="relative z-10 mx-auto flex w-full max-w-6xl flex-1 flex-col">
        <div className="flex justify-end">{signOutControl}</div>

        <div className="grid flex-1 items-center gap-6 py-8 lg:grid-cols-[minmax(0,1fr)_410px] lg:gap-8">
          <section className="relative overflow-hidden rounded-[2.25rem] border border-white/70 bg-white/75 p-7 shadow-[0_28px_80px_-42px_rgba(13,31,45,0.55)] backdrop-blur sm:p-10 lg:p-12">
            <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-brand/35 blur-3xl" />
            <div className="pointer-events-none absolute bottom-8 right-10 hidden h-2 w-24 rounded-full bg-brand lg:block" />

            <div className="relative space-y-8">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#0D1F2D]/10 bg-[#0D1F2D]/[0.03] px-3.5 py-1.5 text-[0.68rem] font-extrabold uppercase tracking-[0.28em] text-[#0D1F2D]/70">
                <span className="h-2 w-2 rounded-full bg-brand shadow-[0_0_18px_rgba(200,255,0,0.9)]" />
                <FormattedMessage id="privateBeta.badge" defaultMessage="Private beta" />
              </div>

              <div className="space-y-5">
                <h1 className="max-w-3xl font-display text-[clamp(2.75rem,7vw,5.8rem)] font-extrabold leading-[0.92] tracking-[-0.06em] text-[#0D1F2D]">
                  <FormattedMessage
                    id="privateBeta.title"
                    defaultMessage="Cheetah is opening by invite."
                  />
                </h1>
                <p className="max-w-2xl text-base leading-7 text-[#0D1F2D]/65 sm:text-lg sm:leading-8">
                  <FormattedMessage
                    id="privateBeta.description"
                    defaultMessage="Share your brand URL and we will review it for the next release wave. If it is a fit, Cheetah starts with your brand already understood."
                  />
                </p>
              </div>

              <div className="grid gap-3 text-sm text-[#0D1F2D]/60 md:grid-cols-3">
                <div className="group rounded-[1.35rem] border border-[#0D1F2D]/10 bg-[#FAFAF7]/80 p-4 transition-all hover:-translate-y-1 hover:border-[#0D1F2D]/20 hover:bg-white">
                  <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-[#0D1F2D]/35">
                    <FormattedMessage id="privateBeta.stepOneTitle" defaultMessage="01. Share" />
                  </p>
                  <p className="mt-3 font-semibold leading-6 text-[#0D1F2D]">
                    <FormattedMessage
                      id="privateBeta.stepOneBody"
                      defaultMessage="Send us the website Cheetah should learn first."
                    />
                  </p>
                </div>
                <div className="group rounded-[1.35rem] border border-[#0D1F2D]/10 bg-[#FAFAF7]/80 p-4 transition-all hover:-translate-y-1 hover:border-[#0D1F2D]/20 hover:bg-white">
                  <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-[#0D1F2D]/35">
                    <FormattedMessage id="privateBeta.stepTwoTitle" defaultMessage="02. Review" />
                  </p>
                  <p className="mt-3 font-semibold leading-6 text-[#0D1F2D]">
                    <FormattedMessage
                      id="privateBeta.stepTwoBody"
                      defaultMessage="We prioritize brands ready to ship campaigns."
                    />
                  </p>
                </div>
                <div className="group rounded-[1.35rem] border border-[#0D1F2D]/10 bg-[#FAFAF7]/80 p-4 transition-all hover:-translate-y-1 hover:border-[#0D1F2D]/20 hover:bg-white">
                  <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-[#0D1F2D]/35">
                    <FormattedMessage id="privateBeta.stepThreeTitle" defaultMessage="03. Access" />
                  </p>
                  <p className="mt-3 font-semibold leading-6 text-[#0D1F2D]">
                    <FormattedMessage
                      id="privateBeta.stepThreeBody"
                      defaultMessage="Approved brands start with sharper context."
                    />
                  </p>
                </div>
              </div>
            </div>
          </section>

          <aside className="relative overflow-hidden rounded-[2rem] bg-[#0D1F2D] p-1 shadow-[0_34px_80px_-38px_rgba(13,31,45,0.85)]">
            <div className="pointer-events-none absolute left-1/2 top-12 h-48 w-48 -translate-x-1/2 rounded-full bg-brand/30 blur-3xl" />
            <div className="relative rounded-[1.75rem] border border-white/10 bg-[#0D1F2D]/95 p-6 text-white sm:p-7">
              <div className="space-y-6">
                <div>
                  <h2 className="font-display text-3xl font-extrabold leading-none tracking-[-0.04em]">
                    <FormattedMessage
                      id="privateBeta.formTitle"
                      defaultMessage="Request early access"
                    />
                  </h2>
                  <p className="mt-3 text-sm leading-6 text-white/60">
                    <FormattedMessage
                      id="privateBeta.formDescription"
                      defaultMessage="We use your site to understand your brand and prioritize the right onboarding path."
                    />
                  </p>
                </div>

                {waitlistEntry ? (
                  <div className="rounded-[1.35rem] border border-brand/25 bg-brand/10 p-4 text-sm">
                    <p className="font-semibold text-white">
                      <FormattedMessage
                        id="privateBeta.joinedTitle"
                        defaultMessage="You are on the list."
                      />
                    </p>
                    <p className="mt-1 leading-6 text-white/65">
                      <FormattedMessage
                        id="privateBeta.joinedBody"
                        defaultMessage="We have {brandUrl}. Update it here if your main site changes."
                        values={{ brandUrl: waitlistEntry.brandUrl }}
                      />
                    </p>
                  </div>
                ) : null}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <label className="block space-y-2.5">
                    <span className="text-xs font-extrabold uppercase tracking-[0.22em] text-white/55">
                      <FormattedMessage id="privateBeta.brandUrlLabel" defaultMessage="Brand URL" />
                    </span>
                    <Input
                      required
                      type="text"
                      inputMode="url"
                      value={brandUrl}
                      onChange={(event) => {
                        setBrandUrl(event.target.value);
                        if (error) {
                          setError("");
                        }
                      }}
                      placeholder={intl.formatMessage({
                        id: "privateBeta.brandUrlPlaceholder",
                        defaultMessage: "https://yourbrand.com",
                      })}
                      aria-invalid={Boolean(error)}
                      aria-describedby={error ? "private-beta-brand-url-error" : undefined}
                      className="h-12 rounded-[1rem] border-white/10 bg-white px-4 text-base font-semibold text-[#0D1F2D] shadow-none placeholder:text-[#0D1F2D]/35 focus-visible:border-brand focus-visible:ring-brand/35 disabled:bg-white/70 md:text-sm"
                      disabled={isSubmitting}
                    />
                  </label>

                  {error ? (
                    <p
                      id="private-beta-brand-url-error"
                      className="text-sm leading-6 text-[#FFB4A9]"
                    >
                      {error}
                    </p>
                  ) : null}

                  <Button
                    type="submit"
                    variant="brand"
                    className="h-12 w-full rounded-[1rem] text-sm font-extrabold tracking-[-0.01em] shadow-[0_18px_36px_-18px_rgba(200,255,0,0.9)] transition-all hover:-translate-y-0.5"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <FormattedMessage id="privateBeta.submitting" defaultMessage="Submitting…" />
                    ) : waitlistEntry ? (
                      <FormattedMessage
                        id="privateBeta.updateButton"
                        defaultMessage="Update request"
                      />
                    ) : (
                      <FormattedMessage
                        id="privateBeta.submitButton"
                        defaultMessage="Request early access"
                      />
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
